import { Button, Card, Empty, Spin, Tooltip } from 'antd';
import clsx from 'clsx';
import type { iconComponents } from '../../Core/IconImports';
import React, { useCallback, useEffect, useRef, useState, type Key } from 'react';
import { useTranslation } from 'react-i18next';
import { AiClassificationStatusEnum } from '../../../enums/AiClassificationStatusEnum';
import { FileLimitationEnum } from '../../../enums/FileLimitationEnum';
import { FileOfflineStatusEnum } from '../../../enums/FileOfflineStatusEnum';
import { FileSalesStatusEnum } from '../../../enums/FileSalesStatusEnum';
import { FilesTableEnum } from '../../../enums/FilesTableEnum';
import { SpecialFolderTypeEnum } from '../../../enums/SpecialFolderTypeEnum';
import { useAuth } from '../../../hooks/useAuth';
import { useFilePassword } from '../../../hooks/useFilePassword';
import { useFileUpload } from '../../../hooks/useFileUpload';
import useFilesDragGhostCard from '../../../hooks/useFilesDragGhostCard';
import type File from '../../../interfaces/File';
import type { FileOffline } from '../../../interfaces/FileApiService';
import type FilesDragged from '../../../interfaces/FilesDragged';
import type TableEmptyState from '../../../interfaces/TableEmptyState';
import {
  buildFilesDragged,
  hasFilesDragged,
  INTERNAL_DND_MIME,
  readFilesDragged,
} from '../../../lib/filesDnd';
import { useAppStore } from '../../../store/store';
import { formatShareExpirationAt } from '../../../utils/fileExpirationUtils';
import {
  getFileTitle,
  getFolderHex,
  hasFileLimitation,
  isImageFileExtension,
  removeInternalFileExtension,
  removeShortcutExtension,
} from '../../../utils/utils';
import FileIcon from '../../Common/FileIcon';
import { Icon } from '../../Core/Icon';
import FileAiClassificationStatusIcon from '../FileAiClassificationStatusIcon';
import FileAntivirusStatusIcon from '../FileAntivirusStatusIcon';
import FilesGridTagsIcon from '../FilesGridTagsIcon/FilesGridTagsIcon';
import FilesImagePreviewGroup from '../FilesImagePreviewGroup';
import { getShareExpirationIconState } from '../FilesTableTitleCell/getShareExpirationIconState';

interface FilesListProps {
  files: File[];
  isLoading: boolean;
  handleLoadMore: () => void;
  scrollHeight?: number;
  handleFileRowClick: (file: File, hasClickedOnFilename: boolean, event?: React.MouseEvent) => void;
  handleRowClick: (file: File, event?: React.MouseEvent) => void;
  selectedTableKeys: Key[];
  setSelectedTableKeys: (keys: Key[]) => void;
  handleRowContextMenu: (record: File, event: React.MouseEvent) => void;
  handleEmptyAreaContextMenu: (event: React.MouseEvent) => void;
  handleRowDoubleClick: (file: File) => void;
  emptyState: TableEmptyState;
  filesOffline: FileOffline[];
  handleMoveDraggedFiles: (files: File[], parentFolder: File | null) => void;
}

/**
 * Grid view of files with infinite scroll, drag-and-drop, and thumbnail image preview carousel.
 */
const FilesList: React.FC<FilesListProps> = ({
  files,
  isLoading,
  handleLoadMore,
  scrollHeight = 0,
  handleRowClick,
  selectedTableKeys,
  setSelectedTableKeys,
  handleRowContextMenu,
  handleEmptyAreaContextMenu,
  handleRowDoubleClick,
  handleFileRowClick,
  emptyState,
  filesOffline,
  handleMoveDraggedFiles,
}) => {
  // Hook for handling file uploads
  const { uploadFiles } = useFileUpload();
  // Hook for route-based logic
  const { isInFileRoute } = useAuth();
  // Translation function for i18n
  const { t } = useTranslation();
  // Set global state for dropzone disabled status
  const { setIsGlobalFilesDropzoneDisabled } = useAppStore();
  const { hasPassword: checkHasPassword } = useFilePassword();

  // State to track which card is currently being dragged over
  const [activeCardId, setActiveCardId] = useState<number | null>(null);
  // State to track which card is currently being dragged
  const [draggingCardId, setDraggingCardId] = useState<number | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  const checkNearBottom = useCallback(() => {
    const scrollableElement = scrollerRef.current;
    if (!scrollableElement) return;
    if (
      scrollableElement.scrollTop + scrollableElement.clientHeight >=
      scrollableElement.scrollHeight - 100
    ) {
      handleLoadMore();
    }
  }, [handleLoadMore]);

  // ponytail: scroll events fire only on movement; re-check after append when user stays at bottom.
  useEffect(() => {
    if (isLoading) return;
    checkNearBottom();
  }, [files.length, isLoading, checkNearBottom]);

  /**
   * Function to determine if drop (upload or move) should be disabled for a given file.
   * Logic: disabled if not a folder, read-only, is in trash, or is a QuickShare file.
   */
  const isDropDisabledInThisFile = (file: File) => {
    return (
      !file.isFolder ||
      file.isReadOnly ||
      isInFileRoute(FilesTableEnum.Trash) ||
      file.specialFolder === SpecialFolderTypeEnum.QuickShare
    );
  };

  /**
   * Returns offline status and icon information for a file, if available.
   * Looks up offline status from `filesOffline` prop using file's id.
   * Provides localized title and icon name for UI display.
   */
  const currentFileOfflineStatus = (file: File) => {
    const currentStatus = filesOffline?.find((offlineFile) => offlineFile.id === file.id)?.status;
    if (currentStatus) {
      switch (currentStatus) {
        case FileOfflineStatusEnum.Pending:
          return {
            title: t(`fileOfflineStatus.${currentStatus}`),
            icon: 'ClockArrowDownIcon' as keyof typeof iconComponents,
          };
        case FileOfflineStatusEnum.Downloading:
          return {
            title: t(`fileOfflineStatus.${currentStatus}`),
            icon: 'HardDriveDownloadIcon' as keyof typeof iconComponents,
          };
        case FileOfflineStatusEnum.Downloaded:
          return {
            title: t(`fileOfflineStatus.${currentStatus}`),
            icon: 'HardDriveIcon' as keyof typeof iconComponents,
          };
      }
    }
  };

  // Hook for showing/hiding the drag ghost element during drag-and-drop
  const { show: showDragGhost, hide: hideDragGhost } = useFilesDragGhostCard();

  // Effect for managing drag state and cleanup handlers for global events
  useEffect(() => {
    // Handler to reset active card when drag is over a global dropzone
    const handleGlobalOver = () => {
      setActiveCardId(null);
    };
    // Placeholder for potential global leave event handler
    const handleGlobalLeave = () => {};

    // Subscribe to store event for global dropzone "drag over"
    const unsubscribeOver = useAppStore
      .getState()
      .events.on.globalDropzoneDragOver(handleGlobalOver);
    // Subscribe to store event for global dropzone "drag leave"
    const unsubscribeLeave = useAppStore
      .getState()
      .events.on.globalDropzoneDragLeave(handleGlobalLeave);

    // Cleanup listeners for drag and drop events at window scope
    const handleWindowEnd = () => setActiveCardId(null);
    window.addEventListener('dragend', handleWindowEnd, true);
    window.addEventListener('drop', handleWindowEnd, true);

    // Cleanup subscriptions and event listeners on unmount
    return () => {
      unsubscribeOver();
      unsubscribeLeave();
      window.removeEventListener('dragend', handleWindowEnd, true);
      window.removeEventListener('drop', handleWindowEnd, true);
    };
  }, []);

  /**
   * Function to test if an internal drag-and-drop is invalid.
   * Disallows drop if:
   *  - There is no payload
   *  - No files in payload
   *  - Target is not a folder
   *  - Attempting to drop a file onto itself
   */
  const isInvalidInternalDrop = (payload: FilesDragged | null, target: File) => {
    if (!payload) return true;
    if (!Array.isArray(payload.files) || payload.files.length === 0) return true;
    if (!target.isFolder) return true;
    if (payload.files.some((f) => f.id === target.id)) return true; // self-drop prevention
    return false;
  };

  /**
   * Handles right-click/context menu on the grid (outside of a file card).
   * Only triggers when clicking an empty area, not a file card.
   */
  const handleGridContextMenu = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    // Check if right click is within a file card (using data attribute or class)
    const isFileCard = target.closest('[data-file-card-id]');

    if (!isFileCard) {
      event.preventDefault();
      handleEmptyAreaContextMenu(event);
    }
  };

  return (
    <FilesImagePreviewGroup files={files}>
      <div
        ref={scrollerRef}
        data-files-grid-scroller
        className={clsx('min-h-0 overflow-y-auto', scrollHeight <= 0 && 'h-full')}
        style={scrollHeight > 0 ? { maxHeight: scrollHeight } : undefined}
        onScroll={checkNearBottom}
        onContextMenu={handleGridContextMenu}
      >
        {files.length === 0 ? (
          isLoading ? (
            <div className="flex justify-center p-8">
              <Spin />
            </div>
          ) : (
            <Empty
              image={<Icon name={emptyState.iconName} className="text-gray-medium h-28 w-28" />}
              description={
                <div className="flex flex-col items-center gap-3">
                  <span className="text-gray-dark">{emptyState.label}</span>
                  {emptyState.variant === 'error' && emptyState.onRetry ? (
                    <Button type="primary" size="small" onClick={emptyState.onRetry}>
                      {t('common.retry')}
                    </Button>
                  ) : null}
                </div>
              }
            />
          )
        ) : (
          <Spin spinning={isLoading}>
            <div
              className="grid grid-cols-2 gap-4 p-4 min-[768px]:grid-cols-3 min-[992px]:grid-cols-4 min-[1200px]:grid-cols-5 min-[1600px]:grid-cols-6 min-[1920px]:grid-cols-8"
              role="listbox"
              aria-multiselectable="true"
            >
              {files.map((item: File) => (
                <Card
                  key={item.id}
                  data-file-card-id={item.id}
                  variant="borderless"
                  className={clsx(
                    'group hover:bg-gray-light relative h-40 w-full overflow-hidden rounded-xl border border-transparent p-2 transition-all duration-200',
                    selectedTableKeys.includes(item.id)
                      ? 'border-blue-dark bg-gray-light border'
                      : '',
                    activeCardId === item.id ? 'border-blue-dark! bg-blue-dark/15! border' : '',
                    draggingCardId === item.id ? 'ring-blue-dark/50 ring-1' : '',
                  )}
                  styles={{ body: { overflow: 'hidden' } }}
                  hoverable
                  draggable={
                    !hasFileLimitation(item.fileLimitations, FileLimitationEnum.DenyFileMove)
                  }
                  // Handle drag start event for cards (sets up drag payload & ghost, disables dropzone globally)
                  onDragStart={(e) => {
                    // Build set of selected IDs for drag
                    const selectedIdSet = new Set(
                      (selectedTableKeys as Array<number | string>)
                        .map((k) => (typeof k === 'number' ? k : Number(k)))
                        .filter((n) => Number.isFinite(n)) as number[],
                    );
                    // If anchor is selected, drag all selected, else just the clicked file
                    const anchorIsSelected = selectedIdSet.has(item.id);
                    const draggedFiles = anchorIsSelected
                      ? files.filter((f) => selectedIdSet.has(f.id))
                      : [item];

                    // Prepare drag payload and set on dataTransfer
                    const payload = buildFilesDragged(draggedFiles, item.id);
                    e.dataTransfer?.setData(INTERNAL_DND_MIME, JSON.stringify(payload));
                    e.dataTransfer?.setData('text/plain', JSON.stringify(payload));
                    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';

                    // Show drag ghost for visual feedback
                    const el = showDragGhost(draggedFiles);
                    if (e.dataTransfer && el) {
                      try {
                        e.dataTransfer.setDragImage(el, 12, 12);
                      } catch {}
                    }
                    // Force browser layout for drag ghost snapshot
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    (el as HTMLElement).offsetHeight;

                    // Disable dropzone globally while dragging files internally
                    setIsGlobalFilesDropzoneDisabled(true);

                    setDraggingCardId(item.id);
                  }}
                  // Handle drag enter: highlight card as drag-over target if allowed
                  onDragEnter={(e) => {
                    if (isDropDisabledInThisFile(item)) return;
                    const nat = e.nativeEvent as DragEvent;
                    const internal = hasFilesDragged(nat);
                    const externalFiles = Array.from(e.dataTransfer?.types ?? []).includes('Files');
                    if (internal || externalFiles) setActiveCardId(item.id);
                  }}
                  // Handle drag over: allow drop if files present
                  onDragOver={(e) => {
                    if (isDropDisabledInThisFile(item)) return;
                    const nat = e.nativeEvent as DragEvent;
                    const internal = hasFilesDragged(nat);
                    const externalFiles = Array.from(e.dataTransfer?.types ?? []).includes('Files');
                    if (!internal && !externalFiles) return;
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.dataTransfer) e.dataTransfer.dropEffect = internal ? 'move' : 'copy';
                    if (activeCardId !== item.id) setActiveCardId(item.id);
                  }}
                  // Handle drag leave from card: clear highlight if pointer leaves card
                  onDragLeave={(e) => {
                    if (isDropDisabledInThisFile(item)) return;
                    e.preventDefault();
                    e.stopPropagation();
                    const { clientX, clientY } = e;
                    // After event, check if pointer is still in this card (prevents flicker)
                    requestAnimationFrame(() => {
                      const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
                      if (el) {
                        const card = el.closest('[data-file-card-id]');
                        if (card && card.getAttribute('data-file-card-id') === String(item.id)) {
                          // Still inside this card, leave highlight on
                          return;
                        }
                      }
                      setActiveCardId((prev) => (prev === item.id ? null : prev));
                    });
                  }}
                  // Handle drop: move or upload files onto the card if allowed
                  onDrop={(e) => {
                    if (isDropDisabledInThisFile(item)) return;
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                      const nat = e.nativeEvent as DragEvent;
                      // Internal drag from files grid
                      if (hasFilesDragged(nat)) {
                        const payload = readFilesDragged(nat);
                        if (isInvalidInternalDrop(payload, item)) return;
                        handleMoveDraggedFiles(payload?.files ?? [], item);
                        // External drag (browser or OS file drop)
                      } else if (Array.from(e.dataTransfer?.types ?? []).includes('Files')) {
                        uploadFiles(
                          e.dataTransfer!.items,
                          false,
                          item.id,
                          undefined,
                          item.isPasswordSet,
                          item.fileName,
                        );
                      }
                    } finally {
                      // Reset drag state after drop
                      setActiveCardId((prev) => (prev === item.id ? null : prev));
                      setDraggingCardId(null);
                      setIsGlobalFilesDropzoneDisabled(false);
                    }
                  }}
                  // Handle drag end: reset drag and drop states, hide drag ghost
                  onDragEnd={() => {
                    setIsGlobalFilesDropzoneDisabled(false);
                    hideDragGhost();
                    setDraggingCardId(null);
                    setActiveCardId((prev) => (prev === item.id ? null : prev));
                  }}
                  // Handle card click: row select/click logic
                  onClick={(event: React.MouseEvent) => handleRowClick(item, event)}
                  // Handle card double-click: usually used for open or preview
                  onDoubleClick={() => handleRowDoubleClick(item)}
                  // Right-click on card opens row-level context menu
                  onContextMenu={(event) => handleRowContextMenu(item, event)}
                  tabIndex={0}
                  role="option"
                  aria-selected={selectedTableKeys.includes(item.id)}
                  onKeyDown={(event: React.KeyboardEvent) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleRowDoubleClick(item);
                      return;
                    }
                    if (event.key === ' ') {
                      event.preventDefault();
                      handleRowClick(item, event as unknown as React.MouseEvent);
                    }
                  }}
                >
                  {/* Drag over overlay (visible only if not disabled and this card is the active drag target) */}
                  {!isDropDisabledInThisFile(item) && activeCardId === item.id && (
                    <div className="border-blue-dark bg-blue-dark/15 pointer-events-none absolute inset-0 z-10 rounded-md border-2" />
                  )}

                  {/* Checkbox in top-right corner for multi-select; only visible on hover or when checked */}
                  <div className="absolute top-2 right-2 z-10">
                    <input
                      type="checkbox"
                      checked={selectedTableKeys.includes(item.id)}
                      onChange={() => {
                        setSelectedTableKeys(
                          selectedTableKeys.includes(item.id)
                            ? selectedTableKeys.filter((key) => key !== item.id)
                            : [...selectedTableKeys, item.id],
                        );
                      }}
                      // Prevent click on checkbox from bubbling up and selecting the card
                      onClick={(e) => e.stopPropagation()}
                      className={clsx(
                        'accent-azure h-5 w-5 cursor-pointer',
                        selectedTableKeys.includes(item.id)
                          ? 'block'
                          : 'hidden group-hover:block [@media(hover:none)]:block',
                      )}
                    />
                  </div>

                  {/* Card content: file icon, filename, and status icons */}
                  <div className="flex h-full w-full flex-col items-center justify-center">
                    <FileIcon
                      fileId={item.id}
                      isFolderPasswordProtected={item.isPasswordSet && !item.isPasswordBypass}
                      isShortcut={item.isShortcut}
                      isFolder={item.isFolder}
                      fileExtension={removeShortcutExtension(item.fileExtension)}
                      secretPreset={item.secretPreset}
                      enableImagePreview={
                        !item.isFolder &&
                        isImageFileExtension(removeShortcutExtension(item.fileExtension))
                      }
                      className="h-20! w-20!"
                      imageClassName="h-20! w-24!"
                      style={{
                        color: getFolderHex(item),
                      }}
                    />

                    <div className="flex w-full min-w-0 flex-row flex-wrap items-center justify-center gap-2 overflow-hidden">
                      {(() => {
                        const fullName = removeInternalFileExtension(
                          removeShortcutExtension(getFileTitle(item)),
                        );
                        const dotIndex = !item.isFolder ? fullName.lastIndexOf('.') : -1;
                        const baseName = dotIndex > 0 ? fullName.slice(0, dotIndex) : fullName;
                        const ext = dotIndex > 0 ? fullName.slice(dotIndex) : '';
                        return (
                          <Tooltip title={fullName}>
                            <p
                              className={clsx(
                                'mt-2 w-full cursor-pointer overflow-hidden px-2 hover:underline',
                                item?.isUnRead && 'font-bold',
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFileRowClick(item, true, e);
                              }}
                            >
                              <span className="flex min-w-0 items-baseline justify-center">
                                <span className="min-w-0 truncate">{baseName}</span>
                                {ext && <span className="shrink-0 whitespace-nowrap">{ext}</span>}
                              </span>
                            </p>
                          </Tooltip>
                        );
                      })()}
                      {/* Row of various status icons (offline, favorite, shared, AI, for sale, editor/viewer) */}
                      <div className="flex w-full flex-row items-center justify-center gap-2">
                        {/* If file has an offline status, show offline status icon with tooltip */}
                        {currentFileOfflineStatus(item) && (
                          <Tooltip title={currentFileOfflineStatus(item)?.title}>
                            <div>
                              <Icon
                                name={
                                  currentFileOfflineStatus(item)
                                    ?.icon as keyof typeof iconComponents
                                }
                                className="text-blue-dark h-5 w-5"
                              />
                            </div>
                          </Tooltip>
                        )}

                        {/* If "favorite", show star icon */}
                        {item?.isFavourite && (
                          <Tooltip title={t('common.favourite')}>
                            <div>
                              <Icon name="StarFullIcon" className="text-blue-dark h-5 w-5" />
                            </div>
                          </Tooltip>
                        )}

                        {/* If public share, show link icon */}
                        {item?.isPublicShare && (
                          <Tooltip title={t('common.publicLinkLabel')}>
                            <div>
                              <Icon name="LinkIcon" className="text-blue-dark h-5 w-5" />
                            </div>
                          </Tooltip>
                        )}

                        {/* If shared, show shared icon (color by share expiration) */}
                        {item?.isShared &&
                          (() => {
                            const shareIconState = getShareExpirationIconState(
                              item.shareExpirationAt,
                              Date.now(),
                            );
                            const shareExpirationDate =
                              item.shareExpirationAt != null && item.shareExpirationAt > 0
                                ? formatShareExpirationAt(item.shareExpirationAt)
                                : '';
                            const shareTooltip =
                              shareIconState.tooltipMode === 'shareExpiredOn'
                                ? t('common.shareExpiredOn', { date: shareExpirationDate })
                                : shareIconState.tooltipMode === 'expiresOn'
                                  ? t('common.expiresOn', { date: shareExpirationDate })
                                  : t('common.shared');
                            return (
                              <Tooltip title={shareTooltip}>
                                <div>
                                  <Icon
                                    name="Share2Icon"
                                    className={`${shareIconState.colorClass} h-5 w-5`}
                                  />
                                </div>
                              </Tooltip>
                            );
                          })()}

                        {/* AI classification status icon */}
                        {item?.aiOperationStatus !== AiClassificationStatusEnum.None && (
                          <FileAiClassificationStatusIcon file={item} />
                        )}
                        <FileAntivirusStatusIcon file={item} />

                        {/* Tags: icon + popover — avoids growing the card with pills */}
                        <FilesGridTagsIcon file={item} />

                        {/* If marked for sale, show store icon */}
                        {item?.salesStatus === FileSalesStatusEnum.ForSale && (
                          <Tooltip title={t('common.forSale')}>
                            <div>
                              <Icon name="StoreIcon" className="text-blue-dark h-5 w-5" />
                            </div>
                          </Tooltip>
                        )}

                        {/* If in Received view, show either the viewer or editor icon */}
                        {isInFileRoute(FilesTableEnum.Received) && (
                          <Tooltip
                            title={item?.isReadOnly ? t('common.viewer') : t('common.editor')}
                          >
                            <div>
                              {item?.isReadOnly ? (
                                <Icon name="ViewIcon" className="text-yellow-dark h-5 w-5" />
                              ) : (
                                <Icon name="SquarePenIcon" className="text-green-dark h-5 w-5" />
                              )}
                            </div>
                          </Tooltip>
                        )}
                        {/* Show lock icon if folder has password */}
                        {item?.isFolder && checkHasPassword(item.id) && (
                          <Tooltip title={t('common.protectedFolder') || 'Protected folder'}>
                            <div>
                              <Icon name="FolderKeyIcon" className="text-blue-dark h-5 w-5" />
                            </div>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Spin>
        )}
      </div>
    </FilesImagePreviewGroup>
  );
};

// Export the FilesList component as default export
export default FilesList;
